/**
 * 🎫 Ticket Detail — vista detallada de un ticket con timeline de comentarios
 * Ruta: /apps/tickets/:ticketId
 */

import { useState, useEffect, useCallback , useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  TextField,
  Select,
  Badge,
  Box,
  Spinner,
  Banner,
  Divider,
  Checkbox,
} from '@/lib/polaris';
import {
  NoteIcon,
  SendIcon,
  EditIcon,
} from '@/lib/polaris-icons';
import { PageBreadcrumbTitle } from '@/components/polaris/PageBreadcrumbTitle';
import { ROUTES } from '@/config/routes.config';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@smarty/shared';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { useParams } from 'react-router-dom';

interface Ticket {
  id: string;
  ticket_number: number;
  subject: string;
  description: string | null;
  status: string;
  priority: string;
  category: string | null;
  tags: string[];
  source: string;
  assigned_to: string | null;
  contact_id: string | null;
  sla_policy_id: string | null;
  first_response_due_at: string | null;
  resolution_due_at: string | null;
  first_responded_at: string | null;
  resolved_at: string | null;
  sla_first_response_met: boolean | null;
  sla_resolution_met: boolean | null;
  satisfaction_rating: number | null;
  satisfaction_comment: string | null;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
}

interface TicketComment {
  id: string;
  body: string;
  comment_type: string;
  is_from_customer: boolean;
  author_id: string | null;
  created_at: string;
}

const STATUS_OPTIONS = [
  { label: 'Nuevo', value: 'new' },
  { label: 'En Progreso', value: 'open' },
  { label: 'Esperando Respuesta', value: 'waiting' },
  { label: 'Resuelto', value: 'resolved' },
  { label: 'Cerrado', value: 'closed' },
];

const PRIORITY_OPTIONS = [
  { label: 'Baja', value: 'low' },
  { label: 'Media', value: 'medium' },
  { label: 'Alta', value: 'high' },
  { label: 'Urgente', value: 'urgent' },
];

const STATUS_TONES: Record<string, 'success' | 'warning' | 'critical' | 'info' | 'new'> = {
  new: 'info', open: 'warning', waiting: 'new', resolved: 'success', closed: 'success',
};

const PRIORITY_TONES: Record<string, 'success' | 'warning' | 'critical' | 'info'> = {
  low: 'info', medium: 'warning', high: 'critical', urgent: 'critical',
};

const COMMENT_TYPE_STYLES: Record<string, { bg: string; label: string }> = {
  reply: { bg: 'bg-surface', label: 'Respuesta' },
  internal_note: { bg: 'bg-surface-warning', label: 'Nota interna' },
  status_change: { bg: 'bg-surface-secondary', label: 'Sistema' },
  assignment: { bg: 'bg-surface-secondary', label: 'Asignación' },
  system: { bg: 'bg-surface-secondary', label: 'Sistema' },
};

export default function TicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const storeNavigate = useStoreNavigate();
  const { tenantId, user } = useAuth();
  // AuthContextValue NO expone `userId` — el id del usuario vive en `user.id`. Leerlo como
  // `userId` daba SIEMPRE undefined, y los guards `if (!userId) return` mataban la feature entera.
  const userId = user?.id;
  const toast = useAdminToast();


  // Reply form
  const [replyBody, setReplyBody] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sending, setSending] = useState(false);

  // Edit fields
  const [editStatus, setEditStatus] = useState('');
  const [editPriority, setEditPriority] = useState('');
  const [updatingField, setUpdatingField] = useState(false);

  // Por la caché (ADR-0012, paso 3). Ticket y comentarios viajan juntos: un ticket sin su hilo no
  // es una pantalla útil, y cachear sólo el ticket la dejaría a medias al volver.
  const consulta = useQuery({
    queryKey: ['ticket', tenantId, ticketId],
    enabled: !!tenantId && !!ticketId,
    staleTime: 30_000,
    queryFn: async (): Promise<{ ticket: Ticket | null; comments: TicketComment[] }> => {
      const [ticketRes, commentsRes] = await Promise.all([
        supabase.from('tickets').select('*').eq('id', ticketId!).single(),
        supabase.from('ticket_comments').select('*').eq('ticket_id', ticketId!).order('created_at'),
      ]);
      if (ticketRes.error) throw ticketRes.error;
      return {
        ticket: (ticketRes.data as Ticket | null) ?? null,
        comments: (commentsRes.data ?? []) as TicketComment[],
      };
    },
  });

  const ticket = consulta.data?.ticket ?? null;
  const comments = useMemo(() => consulta.data?.comments ?? [], [consulta.data]);
  // 🪤 `isPending`, no `isFetching`: con el ticket en pantalla, revalidar no es "cargando".
  const loading = !!tenantId && !!ticketId && consulta.isPending;

  // ⭐ Estado del FORMULARIO sembrado desde el servidor, no derivado de él. Lo que el usuario elige
  // en los selectores es suyo: si se derivara de la consulta, una revalidación en segundo plano le
  // pisaría la selección mientras la está por guardar. Se siembra cuando llega el ticket y después
  // manda el usuario.
  useEffect(() => {
    if (consulta.data?.ticket) {
      setEditStatus(consulta.data.ticket.status);
      setEditPriority(consulta.data.ticket.priority);
    }
  }, [consulta.data?.ticket]);

  // 🪤 v5 sacó `onError`: avisar en el render sería un efecto en medio del render.
  useEffect(() => {
    if (consulta.error) toast.error('Ticket no encontrado');
  }, [consulta.error, toast]);

  const loadTicket = useCallback(async () => { await consulta.refetch(); }, [consulta]);



  // ─── Send reply ───────────────────────────────────────────
  const handleSendReply = async () => {
    if (!ticketId || !tenantId || !replyBody.trim()) return;
    setSending(true);

    try {
      const { error } = await supabase.from('ticket_comments').insert({
        tenant_id: tenantId,
        ticket_id: ticketId,
        body: replyBody.trim(),
        comment_type: isInternal ? 'internal_note' : 'reply',
        author_id: userId,
      });
      if (error) throw error;

      // If first reply, record first response
      if (ticket && !ticket.first_responded_at && !isInternal) {
        const now = new Date().toISOString();
        const met = ticket.first_response_due_at ? new Date(ticket.first_response_due_at) >= new Date(now) : null;
        await supabase.from('tickets').update({
          first_responded_at: now,
          sla_first_response_met: met,
          status: ticket.status === 'new' ? 'open' : ticket.status,
        }).eq('id', ticketId);
      }

      toast.success(isInternal ? 'Nota interna agregada' : 'Respuesta enviada');
      setReplyBody('');
      setIsInternal(false);
      await loadTicket();
    } catch (error: unknown) {
      toast.error('Error al enviar', getErrorMessage(error));
    } finally {
      setSending(false);
    }
  };

  // ─── Update field ─────────────────────────────────────────
  const handleUpdateField = async (field: string, value: string) => {
    if (!ticketId || !tenantId) return;
    setUpdatingField(true);

    const updates: Record<string, unknown> = { [field]: value };

    // Auto-set timestamps
    if (field === 'status' && value === 'resolved') {
      updates.resolved_at = new Date().toISOString();
      if (ticket?.resolution_due_at) {
        updates.sla_resolution_met = new Date(ticket.resolution_due_at) >= new Date();
      }
    }
    if (field === 'status' && value === 'closed') {
      updates.closed_at = new Date().toISOString();
    }

    try {
      const { error } = await supabase.from('tickets').update(updates).eq('id', ticketId);
      if (error) throw error;

      // Log status change as comment
      if (field === 'status') {
        await supabase.from('ticket_comments').insert({
          tenant_id: tenantId,
          ticket_id: ticketId,
          body: `Estado cambiado a ${STATUS_OPTIONS.find(o => o.value === value)?.label ?? value}`,
          comment_type: 'status_change',
          author_id: userId,
        });
      }

      toast.success('Actualizado');
      await loadTicket();
    } catch (error: unknown) {
      toast.error('Error al actualizar', getErrorMessage(error));
    } finally {
      setUpdatingField(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('es-PE', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  // ─── Render ───────────────────────────────────────────────
  if (loading) {
    return <Page title="Cargando..."><Card><Box padding="800"><InlineStack align="center"><Spinner size="large" /></InlineStack></Box></Card></Page>;
  }

  if (!ticket) {
    return <Page title="Ticket no encontrado"><Banner tone="critical"><p>El ticket no existe o no tienes acceso.</p></Banner></Page>;
  }

  return (
    <Page
      title={
        <PageBreadcrumbTitle
          icon={NoteIcon}
          title={`#${ticket.ticket_number} — ${ticket.subject}`}
          onBack={() => storeNavigate(ROUTES.APPS_TICKETS)}
          backAccessibilityLabel="Volver a tickets"
        />
      }
    >
      <BlockStack gap="400">
        {/* Header info */}
        <Card>
          <BlockStack gap="300">
            <InlineStack gap="200" wrap>
              <Badge tone={STATUS_TONES[ticket.status] ?? 'new'}>
                {STATUS_OPTIONS.find(o => o.value === ticket.status)?.label ?? ticket.status}
              </Badge>
              <Badge tone={PRIORITY_TONES[ticket.priority] ?? 'info'}>
                {PRIORITY_OPTIONS.find(o => o.value === ticket.priority)?.label ?? ticket.priority}
              </Badge>
              {ticket.category && <Badge>{ticket.category}</Badge>}
              <Text as="span" tone="subdued" variant="bodySm">
                Origen: {ticket.source} · Creado: {formatDate(ticket.created_at)}
              </Text>
            </InlineStack>

            {ticket.description && (
              <>
                <Divider />
                <Text as="p">{ticket.description}</Text>
              </>
            )}

            {/* SLA info */}
            {(ticket.first_response_due_at || ticket.resolution_due_at) && (
              <>
                <Divider />
                <InlineStack gap="400" wrap>
                  {ticket.first_response_due_at && (
                    <BlockStack gap="050">
                      <Text as="span" variant="bodySm" tone="subdued">Primera respuesta</Text>
                      <InlineStack gap="100">
                        <Text as="span" variant="bodySm">{formatDate(ticket.first_response_due_at)}</Text>
                        {ticket.sla_first_response_met === true && <Badge tone="success">Cumplido</Badge>}
                        {ticket.sla_first_response_met === false && <Badge tone="critical">Excedido</Badge>}
                        {ticket.sla_first_response_met === null && !ticket.first_responded_at && <Badge tone="warning">Pendiente</Badge>}
                      </InlineStack>
                    </BlockStack>
                  )}
                  {ticket.resolution_due_at && (
                    <BlockStack gap="050">
                      <Text as="span" variant="bodySm" tone="subdued">Resolución</Text>
                      <InlineStack gap="100">
                        <Text as="span" variant="bodySm">{formatDate(ticket.resolution_due_at)}</Text>
                        {ticket.sla_resolution_met === true && <Badge tone="success">Cumplido</Badge>}
                        {ticket.sla_resolution_met === false && <Badge tone="critical">Excedido</Badge>}
                        {ticket.resolved_at === null && <Badge tone="warning">Pendiente</Badge>}
                      </InlineStack>
                    </BlockStack>
                  )}
                </InlineStack>
              </>
            )}

            {/* Quick actions */}
            <Divider />
            <InlineStack gap="200" wrap>
              <Box minWidth="160px">
                <Select
                  label="Estado"
                  labelHidden
                  options={STATUS_OPTIONS}
                  value={editStatus}
                  onChange={(v) => { setEditStatus(v); handleUpdateField('status', v); }}
                  disabled={updatingField}
                />
              </Box>
              <Box minWidth="140px">
                <Select
                  label="Prioridad"
                  labelHidden
                  options={PRIORITY_OPTIONS}
                  value={editPriority}
                  onChange={(v) => { setEditPriority(v); handleUpdateField('priority', v); }}
                  disabled={updatingField}
                />
              </Box>
            </InlineStack>
          </BlockStack>
        </Card>

        {/* Timeline */}
        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">Conversación</Text>

            {comments.length === 0 ? (
              <Text as="p" tone="subdued">Sin comentarios aún.</Text>
            ) : (
              <BlockStack gap="300">
                {comments.map((comment) => {
                  const style = COMMENT_TYPE_STYLES[comment.comment_type] ?? COMMENT_TYPE_STYLES.reply;
                  const isSystem = ['status_change', 'assignment', 'system'].includes(comment.comment_type);

                  if (isSystem) {
                    return (
                      <Box key={comment.id} padding="200">
                        <InlineStack gap="200" blockAlign="center">
                          <Text as="span" tone="subdued" variant="bodySm">
                            {comment.body} · {formatDate(comment.created_at)}
                          </Text>
                        </InlineStack>
                      </Box>
                    );
                  }

                  return (
                    <Box
                      key={comment.id}
                      padding="300"
                      background={comment.comment_type === 'internal_note' ? 'bg-surface-warning' : 'bg-surface-secondary'}
                      borderRadius="200"
                    >
                      <BlockStack gap="200">
                        <InlineStack align="space-between">
                          <InlineStack gap="200" blockAlign="center">
                            {comment.comment_type === 'internal_note' && <Badge tone="warning">Nota interna</Badge>}
                            {comment.is_from_customer && <Badge>Cliente</Badge>}
                          </InlineStack>
                          <Text as="span" tone="subdued" variant="bodySm">
                            {formatDate(comment.created_at)}
                          </Text>
                        </InlineStack>
                        <Text as="p">{comment.body}</Text>
                      </BlockStack>
                    </Box>
                  );
                })}
              </BlockStack>
            )}

            {/* Reply form */}
            <Divider />
            <BlockStack gap="200">
              <TextField
                label="Responder"
                labelHidden
                value={replyBody}
                onChange={setReplyBody}
                placeholder={isInternal ? 'Escribir nota interna (no visible al cliente)...' : 'Escribir respuesta al cliente...'}
                multiline={3}
                autoComplete="off"
              />
              <InlineStack align="space-between" blockAlign="center">
                <Checkbox
                  label="Nota interna"
                  checked={isInternal}
                  onChange={setIsInternal}
                  helpText="Solo visible para agentes"
                />
                <Button
                  icon={SendIcon}
                  onClick={handleSendReply}
                  loading={sending}
                  disabled={!replyBody.trim()}
                  variant="primary"
                >
                  {isInternal ? 'Agregar nota' : 'Enviar respuesta'}
                </Button>
              </InlineStack>
            </BlockStack>
          </BlockStack>
        </Card>

        {/* Satisfaction (if resolved) */}
        {ticket.satisfaction_rating && (
          <Card>
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd">Satisfacción del cliente</Text>
              <InlineStack gap="200" blockAlign="center">
                <Text as="span" variant="headingLg">{'⭐'.repeat(ticket.satisfaction_rating)}</Text>
                <Text as="span" tone="subdued">{ticket.satisfaction_rating}/5</Text>
              </InlineStack>
              {ticket.satisfaction_comment && <Text as="p">{ticket.satisfaction_comment}</Text>}
            </BlockStack>
          </Card>
        )}
      </BlockStack>
    </Page>
  );
}
